import { useEffect, useState } from "react";
import { Search, UserPlus, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { getApiErrorMessage } from "@/lib/apiError";

function digitsOnly(value) {
    return String(value || "").replace(/\D/g, "");
}

function formatPhoneInput(value) {
    let digits = digitsOnly(value);
    if (digits.length > 10 && digits.startsWith("1")) {
        digits = digits.slice(1);
    }
    digits = digits.slice(0, 10);

    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isValidPhone(value) {
    if (!value) return true;
    let digits = digitsOnly(value);
    if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
    return digits.length === 10;
}

function formatZipInput(value) {
    const digits = digitsOnly(value).slice(0, 9);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

function isValidZip(value) {
    if (!value) return true;
    const digits = digitsOnly(value);
    return digits.length === 5 || digits.length === 9;
}

function formatStateInput(value) {
    return String(value || "").replace(/[^a-z]/gi, "").toUpperCase().slice(0, 2);
}

function isValidEmail(value) {
    if (!value) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());
}

function normalizeContactPayload(form) {
    return {
        ...form,
        first_name: String(form.first_name || "").trim(),
        last_name: String(form.last_name || "").trim(),
        phone: form.phone ? formatPhoneInput(form.phone) : "",
        email: form.email ? String(form.email).trim().toLowerCase() : "",
        current_state: form.current_state ? formatStateInput(form.current_state) : "",
        current_zip: form.current_zip ? formatZipInput(form.current_zip) : "",
        mailing_state: form.mailing_state ? formatStateInput(form.mailing_state) : "",
        mailing_zip: form.mailing_zip ? formatZipInput(form.mailing_zip) : "",
        co_buyer_phone: form.co_buyer_phone ? formatPhoneInput(form.co_buyer_phone) : "",
        co_buyer_email: form.co_buyer_email ? String(form.co_buyer_email).trim().toLowerCase() : "",
    };
}

function CustomerForm({ initial = {}, onSaved, submitLabel = "Save" }) {
    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        current_address: "",
        current_city: "",
        current_state: "FL",
        current_zip: "",
        mailing_same_as_current: true,
        mailing_address: "",
        mailing_city: "",
        mailing_state: "",
        mailing_zip: "",
        has_co_buyer: false,
        co_buyer_first_name: "",
        co_buyer_last_name: "",
        co_buyer_phone: "",
        co_buyer_email: "",
        ...initial,
        phone: initial.phone ? formatPhoneInput(initial.phone) : "",
        email: initial.email ? String(initial.email).trim().toLowerCase() : "",
        current_state: initial.current_state ? formatStateInput(initial.current_state) : (initial.current_state ?? "FL"),
        current_zip: initial.current_zip ? formatZipInput(initial.current_zip) : "",
        mailing_state: initial.mailing_state ? formatStateInput(initial.mailing_state) : "",
        mailing_zip: initial.mailing_zip ? formatZipInput(initial.mailing_zip) : "",
        co_buyer_phone: initial.co_buyer_phone ? formatPhoneInput(initial.co_buyer_phone) : "",
        co_buyer_email: initial.co_buyer_email ? String(initial.co_buyer_email).trim().toLowerCase() : "",
    });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        const payload = normalizeContactPayload(form);

        if (!payload.first_name || !payload.last_name) {
            toast.error("First and last name are required");
            return;
        }
        if (!isValidPhone(payload.phone)) {
            toast.error("Customer phone must be a 10-digit number");
            return;
        }
        if (!isValidEmail(payload.email)) {
            toast.error("Customer email format is invalid");
            return;
        }
        if (!isValidZip(payload.current_zip)) {
            toast.error("Current ZIP must be 5 or 9 digits");
            return;
        }
        if (!payload.mailing_same_as_current && !isValidZip(payload.mailing_zip)) {
            toast.error("Mailing ZIP must be 5 or 9 digits");
            return;
        }
        if (payload.has_co_buyer && !isValidPhone(payload.co_buyer_phone)) {
            toast.error("Co-buyer phone must be a 10-digit number");
            return;
        }
        if (payload.has_co_buyer && !isValidEmail(payload.co_buyer_email)) {
            toast.error("Co-buyer email format is invalid");
            return;
        }

        try {
            const res = initial?.id
                ? await api.patch(`/customers/${initial.id}`, payload)
                : await api.post("/customers", payload);
            toast.success(initial?.id ? "Customer updated" : "Customer created");
            onSaved?.(res.data);
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Save failed"));
        }
    };

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500">First name</Label>
                    <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="customer-form-first-name-input" />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500">Last name</Label>
                    <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="customer-form-last-name-input" />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500">Phone</Label>
                    <Input
                        type="tel"
                        inputMode="numeric"
                        placeholder="727-597-9622"
                        maxLength={12}
                        value={form.phone || ""}
                        onChange={(e) => set("phone", formatPhoneInput(e.target.value))}
                        data-testid="customer-form-phone-input"
                    />
                    <div className="mt-1 text-[11px] text-slate-500">10 digits required. Extra digits are blocked.</div>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500">Email</Label>
                    <Input
                        type="email"
                        inputMode="email"
                        placeholder="customer@example.com"
                        value={form.email || ""}
                        onChange={(e) => set("email", e.target.value)}
                        data-testid="customer-form-email-input"
                    />
                </div>
            </div>

            <div className="space-y-3">
                <div className="text-xs uppercase tracking-wide text-slate-500">Current Address</div>
                <Input placeholder="Street" value={form.current_address || ""} onChange={(e) => set("current_address", e.target.value)} data-testid="customer-form-address-input" />
                <div className="grid grid-cols-3 gap-3">
                    <Input placeholder="City" value={form.current_city || ""} onChange={(e) => set("current_city", e.target.value)} />
                    <Input placeholder="State" maxLength={2} value={form.current_state || ""} onChange={(e) => set("current_state", formatStateInput(e.target.value))} />
                    <Input placeholder="ZIP" inputMode="numeric" maxLength={10} value={form.current_zip || ""} onChange={(e) => set("current_zip", formatZipInput(e.target.value))} />
                </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2">
                <div>
                    <div className="text-sm font-medium">Mailing address same as current?</div>
                    <div className="text-xs text-slate-500">Toggle off if it's different</div>
                </div>
                <Switch checked={!!form.mailing_same_as_current} onCheckedChange={(v) => set("mailing_same_as_current", v)} data-testid="customer-form-mailing-same-switch" />
            </div>
            {!form.mailing_same_as_current && (
                <div className="space-y-3">
                    <Input placeholder="Mailing street" value={form.mailing_address || ""} onChange={(e) => set("mailing_address", e.target.value)} />
                    <div className="grid grid-cols-3 gap-3">
                        <Input placeholder="City" value={form.mailing_city || ""} onChange={(e) => set("mailing_city", e.target.value)} />
                        <Input placeholder="State" maxLength={2} value={form.mailing_state || ""} onChange={(e) => set("mailing_state", formatStateInput(e.target.value))} />
                        <Input placeholder="ZIP" inputMode="numeric" maxLength={10} value={form.mailing_zip || ""} onChange={(e) => set("mailing_zip", formatZipInput(e.target.value))} />
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2">
                <div>
                    <div className="text-sm font-medium">Co-buyer?</div>
                    <div className="text-xs text-slate-500">Add a second person on the loan/contract</div>
                </div>
                <Switch checked={!!form.has_co_buyer} onCheckedChange={(v) => set("has_co_buyer", v)} data-testid="customer-form-has-co-buyer-switch" />
            </div>
            {form.has_co_buyer && (
                <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Co-buyer first" value={form.co_buyer_first_name || ""} onChange={(e) => set("co_buyer_first_name", e.target.value)} />
                    <Input placeholder="Co-buyer last" value={form.co_buyer_last_name || ""} onChange={(e) => set("co_buyer_last_name", e.target.value)} />
                    <Input
                        type="tel"
                        inputMode="numeric"
                        placeholder="Co-buyer phone"
                        maxLength={12}
                        value={form.co_buyer_phone || ""}
                        onChange={(e) => set("co_buyer_phone", formatPhoneInput(e.target.value))}
                    />
                    <Input
                        type="email"
                        inputMode="email"
                        placeholder="Co-buyer email"
                        value={form.co_buyer_email || ""}
                        onChange={(e) => set("co_buyer_email", e.target.value)}
                    />
                </div>
            )}
            <div className="flex justify-end">
                <Button onClick={submit} className="bg-[#0F2A47] hover:bg-[#14385E] text-white" data-testid="customer-form-submit-button">
                    {submitLabel}
                </Button>
            </div>
        </div>
    );
}

export { CustomerForm };

export default function StepCustomer({ customer, onSelect }) {
    const [q, setQ] = useState("");
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);

    const search = (term) => {
        api.get("/customers", { params: term ? { q: term } : {} }).then((r) => setList(r.data));
    };
    useEffect(() => { search(""); }, []);

    return (
        <div className="space-y-4" data-testid="step-customer">
            <Card className="rounded-xl border-slate-200 p-5">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-sm font-semibold text-slate-900">Select or create a customer</div>
                        <div className="text-xs text-slate-500">
                            Customer info is reused everywhere — enter it once.
                        </div>
                    </div>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-[#0F2A47] hover:bg-[#14385E] text-white" data-testid="step-customer-new-button">
                                <UserPlus className="mr-2 h-4 w-4" /> New customer
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                            <DialogHeader><DialogTitle>New customer</DialogTitle></DialogHeader>
                            <CustomerForm onSaved={(c) => { setOpen(false); onSelect(c); search(""); }} submitLabel="Create customer" />
                        </DialogContent>
                    </Dialog>
                </div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                        value={q}
                        onChange={(e) => { setQ(e.target.value); search(e.target.value); }}
                        placeholder="Search by name, email, phone…"
                        className="pl-9"
                        data-testid="step-customer-search-input"
                    />
                </div>
            </Card>

            <div className="space-y-2">
                {list.length === 0 && (
                    <Card className="p-8 text-center text-sm text-slate-500">
                        No customers yet. Click "New customer" to create one.
                    </Card>
                )}
                {list.map((c) => {
                    const selected = customer?.id === c.id;
                    return (
                        <button
                            key={c.id}
                            type="button"
                            onClick={() => onSelect(c)}
                            className={`w-full text-left rounded-xl border bg-white p-4 transition-colors duration-150 hover:border-[#1E6FA8] ${
                                selected ? "border-[#1E6FA8] bg-[rgba(67,161,215,0.05)]" : "border-slate-200"
                            }`}
                            data-testid={`step-customer-row-${c.id}`}
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-medium text-slate-900">{c.first_name} {c.last_name}</div>
                                    <div className="text-xs text-slate-500">{c.phone || "—"} · {c.email || "—"}</div>
                                </div>
                                <ChevronRight className="h-4 w-4 text-slate-400" />
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
